import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { User, Lock, Eye, EyeOff, AlertCircle, ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerLogin = () => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showPassword, setShowPassword] = useState(false);
  
  // Verificação de e-mail
  const [showVerification, setShowVerification] = useState(false);
  const [verificationCpf, setVerificationCpf] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationEmail, setVerificationEmail] = useState('');
  
  // Login form
  const [loginData, setLoginData] = useState({ cpf: '', password: '' });
  
  // Register form
  const [registerData, setRegisterData] = useState({
    cpf: '',
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    phone: '',
    birthDay: '',
    birthMonth: '',
    birthYear: '',
    rg: '',
    gender: ''
  });

  // Forgot password
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotData, setForgotData] = useState({ cpf: '', email: '' });

  // Se veio de /cliente/esqueci-senha (legacy) via query ?forgot=1, já abre o modal
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('forgot') === '1') setShowForgotPassword(true);
  }, []);

  const formatCPF = (value) => {
    const numbers = value.replace(/\D/g, '').slice(0, 11);
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `${numbers.slice(0, 3)}.${numbers.slice(3)}`;
    if (numbers.length <= 9) return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`;
    return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9)}`;
  };

  // Função para mascarar email: mostra 5 primeiros + ... + @dominio
  const maskEmail = (email) => {
    if (!email || !email.includes('@')) return email;
    const [localPart, domain] = email.split('@');
    const visibleChars = Math.min(5, localPart.length);
    const maskedLocal = localPart.slice(0, visibleChars) + '...';
    return `${maskedLocal}@${domain}`;
  };

  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '').slice(0, 11);
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginData.cpf || !loginData.password) {
      setMessage({ type: 'error', text: 'Preencha todos os campos' });
      return;
    }

    try {
      setLoading(true);
      setMessage({ type: '', text: '' });

      const response = await axios.post(`${BACKEND_URL}/api/customers/login`, {
        cpf: loginData.cpf,
        password: loginData.password
      });

      // Salvar dados do cliente no localStorage (persistente)
      localStorage.setItem('customerData', JSON.stringify(response.data.customer));
      localStorage.setItem('customerToken', response.data.token || 'authenticated');
      
      // Redirecionar para área do cliente
      navigate('/minha-conta');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erro ao fazer login';
      const isFirstAccess = error.response?.headers?.['x-password-not-set'] === 'true' || errorMsg.includes('ainda não definiu');
      
      if (isFirstAccess) {
        // Cliente vindo de compra sem senha — abre fluxo de primeiro acesso
        setForgotData({ ...forgotData, cpf: loginData.cpf });
        setShowForgotPassword(true);
        setMessage({ type: 'warning', text: 'Primeiro acesso? Solicite o link para definir sua senha.' });
      } else if (error.response?.status === 403 && errorMsg.includes('não verificado')) {
        setVerificationCpf(loginData.cpf);
        setShowVerification(true);
        setMessage({ type: 'warning', text: 'Você precisa verificar seu e-mail. Digite o código enviado.' });
      } else {
        setMessage({ type: 'error', text: errorMsg });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    if (!verificationCode || verificationCode.length !== 6) {
      setMessage({ type: 'error', text: 'Digite o código de 6 dígitos' });
      return;
    }

    try {
      setLoading(true);
      setMessage({ type: '', text: '' });

      await axios.post(`${BACKEND_URL}/api/customers/verify-email?cpf=${verificationCpf}&code=${verificationCode}`);
      
      setMessage({ type: 'success', text: 'E-mail verificado! Agora faça login.' });
      setShowVerification(false);
      setVerificationCode('');
      setIsLogin(true);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Código inválido';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      setLoading(true);
      await axios.post(`${BACKEND_URL}/api/customers/resend-verification?cpf=${verificationCpf}`);
      setMessage({ type: 'success', text: 'Novo código enviado para seu e-mail!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Erro ao reenviar código' });
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Validações
    if (!registerData.cpf || !registerData.email || !registerData.password || 
        !registerData.fullName || !registerData.phone || 
        !registerData.birthDay || !registerData.birthMonth || !registerData.birthYear) {
      setMessage({ type: 'error', text: 'Preencha todos os campos obrigatórios' });
      return;
    }

    if (registerData.password !== registerData.confirmPassword) {
      setMessage({ type: 'error', text: 'As senhas não conferem' });
      return;
    }

    if (registerData.password.length < 6) {
      setMessage({ type: 'error', text: 'A senha deve ter pelo menos 6 caracteres' });
      return;
    }

    try {
      setLoading(true);
      setMessage({ type: '', text: '' });

      const birthDate = `${registerData.birthDay}/${registerData.birthMonth}/${registerData.birthYear}`;

      const response = await axios.post(`${BACKEND_URL}/api/customers/register`, {
        cpf: registerData.cpf,
        email: registerData.email,
        password: registerData.password,
        full_name: registerData.fullName,
        phone: registerData.phone,
        birth_date: birthDate,
        rg: registerData.rg || null,
        gender: registerData.gender || null
      });

      // Mostrar tela de verificação
      if (response.data.requires_verification) {
        setVerificationCpf(registerData.cpf);
        setVerificationEmail(registerData.email);
        setShowVerification(true);
        setMessage({ type: 'success', text: 'Cadastro realizado! Verifique seu e-mail e digite o código de confirmação.' });
      } else {
        setMessage({ type: 'success', text: 'Cadastro realizado com sucesso! Faça login para continuar.' });
        setIsLogin(true);
        setLoginData({ cpf: registerData.cpf, password: '' });
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erro ao cadastrar';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();

    if (!forgotData.cpf) {
      setMessage({ type: 'error', text: 'Preencha o CPF' });
      return;
    }

    try {
      setLoading(true);
      setMessage({ type: '', text: '' });

      await axios.post(`${BACKEND_URL}/api/customers/forgot-password`, {
        cpf: forgotData.cpf,
        email: forgotData.email || undefined,
      });

      setMessage({ type: 'success', text: 'Se o CPF estiver cadastrado, enviaremos um link no e-mail cadastrado na sua compra. Verifique também a caixa de spam.' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Erro ao processar solicitação' });
    } finally {
      setLoading(false);
    }
  };

  const days = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0'));
  const months = [
    { value: '01', label: 'Janeiro' },
    { value: '02', label: 'Fevereiro' },
    { value: '03', label: 'Março' },
    { value: '04', label: 'Abril' },
    { value: '05', label: 'Maio' },
    { value: '06', label: 'Junho' },
    { value: '07', label: 'Julho' },
    { value: '08', label: 'Agosto' },
    { value: '09', label: 'Setembro' },
    { value: '10', label: 'Outubro' },
    { value: '11', label: 'Novembro' },
    { value: '12', label: 'Dezembro' }
  ];
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => String(currentYear - i));

  return (
    <div className="min-h-screen pt-20 bg-gray-50">
      <div className="max-w-md mx-auto px-4 py-8">
        <Link to="/" className="inline-flex items-center text-[#003366] hover:underline mb-6">
          <ArrowLeft className="w-4 h-4 mr-1" />
          Voltar para o site
        </Link>

        <Card>
          <CardContent className="p-6">
            <div className="text-center mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_419069bb-d46c-413f-9dd8-09d0cbbf3735/artifacts/rwf0qmt2_LOGO.png" 
                alt="TÔ INDO ALI" 
                className="h-16 w-16 mx-auto mb-4"
              />
              <h1 className="text-2xl font-bold text-gray-900">
                {showForgotPassword ? 'Recuperar Senha' : isLogin ? 'Área do Cliente' : 'Criar Conta'}
              </h1>
              <p className="text-gray-600 text-sm mt-1">
                {showForgotPassword 
                  ? 'Digite seu CPF e e-mail para recuperar a senha'
                  : isLogin 
                    ? 'Acesse sua conta para ver suas viagens' 
                    : 'Cadastre-se para facilitar suas compras'}
              </p>
            </div>

            {message.text && (
              <div className={`mb-4 p-3 rounded-lg flex items-start gap-2 ${
                message.type === 'success' ? 'bg-green-50 text-green-800' : 
                message.type === 'warning' ? 'bg-yellow-50 text-yellow-800' :
                'bg-red-50 text-red-800'
              }`}>
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span className="text-sm">{message.text}</span>
              </div>
            )}

            {/* Tela de verificação de e-mail */}
            {showVerification ? (
              <div className="space-y-4">
                <div className="text-center mb-4">
                  <Mail className="w-16 h-16 mx-auto text-[#003366] mb-4" />
                  <h2 className="text-lg font-semibold text-gray-900">Verifique seu E-mail</h2>
                  <p className="text-sm text-gray-600 mt-2">
                    Enviamos um código de 6 dígitos para:<br/>
                    <span className="font-semibold text-[#003366]">{maskEmail(verificationEmail)}</span><br/>
                    Digite-o abaixo para confirmar sua conta.
                  </p>
                </div>
                
                <form onSubmit={handleVerifyEmail} className="space-y-4">
                  <div>
                    <Label>Código de Verificação</Label>
                    <Input
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      className="mt-1 text-center text-2xl tracking-widest font-mono"
                      maxLength={6}
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-[#003366] hover:bg-[#002244]"
                    disabled={loading || verificationCode.length !== 6}
                  >
                    {loading ? 'Verificando...' : 'Confirmar E-mail'}
                  </Button>
                </form>

                <div className="text-center space-y-2 mt-4">
                  <p className="text-sm text-gray-500">Não recebeu o código?</p>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleResendCode}
                    disabled={loading}
                  >
                    Reenviar código
                  </Button>
                </div>

                <Button 
                  variant="ghost" 
                  className="w-full mt-4"
                  onClick={() => {
                    setShowVerification(false);
                    setVerificationCode('');
                    setMessage({ type: '', text: '' });
                  }}
                >
                  Voltar
                </Button>
              </div>
            ) : showForgotPassword ? (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <p className="text-xs text-gray-600 bg-blue-50 border border-blue-200 p-3 rounded" data-testid="forgot-info">
                  Informe seu CPF. Enviaremos o link para o e-mail cadastrado na sua compra.<br />
                  <strong>Primeiro acesso?</strong> Use este mesmo formulário para definir sua senha.
                </p>
                <div>
                  <Label>CPF</Label>
                  <Input
                    value={forgotData.cpf}
                    onChange={(e) => setForgotData({ ...forgotData, cpf: formatCPF(e.target.value) })}
                    placeholder="000.000.000-00"
                    className="mt-1"
                    data-testid="forgot-cpf-input"
                  />
                </div>
                <div>
                  <Label>E-mail (opcional)</Label>
                  <Input
                    type="email"
                    value={forgotData.email}
                    onChange={(e) => setForgotData({ ...forgotData, email: e.target.value })}
                    placeholder="Se lembrar, preencha para confirmação"
                    className="mt-1"
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-[#003366] hover:bg-[#002244]" data-testid="forgot-submit">
                  {loading ? 'Enviando...' : 'Enviar Link por E-mail'}
                </Button>
                <Button type="button" variant="ghost" onClick={() => setShowForgotPassword(false)} className="w-full">
                  Voltar para login
                </Button>
              </form>
            ) : isLogin ? (
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label>CPF</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      value={loginData.cpf}
                      onChange={(e) => setLoginData({ ...loginData, cpf: formatCPF(e.target.value) })}
                      placeholder="000.000.000-00"
                      className="pl-10"
                    />
                  </div>
                </div>
                <div>
                  <Label>Senha</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      placeholder="Sua senha"
                      className="pl-10 pr-10"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => { setForgotData({ ...forgotData, cpf: loginData.cpf }); setShowForgotPassword(true); setMessage({ type: '', text: '' }); }}
                  className="text-sm text-[#003366] hover:underline text-left"
                  data-testid="forgot-password-btn"
                >
                  Esqueci minha senha / Primeiro acesso
                </button>
                <Button type="submit" disabled={loading} className="w-full bg-[#FFD700] hover:bg-[#FFC700] text-gray-900">
                  {loading ? 'Entrando...' : 'Entrar'}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <Label>Nome Completo *</Label>
                  <Input
                    value={registerData.fullName}
                    onChange={(e) => setRegisterData({ ...registerData, fullName: e.target.value })}
                    placeholder="Seu nome completo"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>CPF *</Label>
                  <Input
                    value={registerData.cpf}
                    onChange={(e) => setRegisterData({ ...registerData, cpf: formatCPF(e.target.value) })}
                    placeholder="000.000.000-00"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>RG</Label>
                  <Input
                    value={registerData.rg}
                    onChange={(e) => setRegisterData({ ...registerData, rg: e.target.value })}
                    placeholder="Número do RG"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>E-mail *</Label>
                  <Input
                    type="email"
                    value={registerData.email}
                    onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                    placeholder="seu@email.com"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>WhatsApp *</Label>
                  <Input
                    value={registerData.phone}
                    onChange={(e) => setRegisterData({ ...registerData, phone: formatPhone(e.target.value) })}
                    placeholder="(00) 00000-0000"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Data de Nascimento *</Label>
                  <div className="grid grid-cols-3 gap-2 mt-1">
                    <select
                      value={registerData.birthDay}
                      onChange={(e) => setRegisterData({ ...registerData, birthDay: e.target.value })}
                      className="border rounded-md px-3 py-2 text-sm"
                    >
                      <option value="">Dia</option>
                      {days.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                    <select
                      value={registerData.birthMonth}
                      onChange={(e) => setRegisterData({ ...registerData, birthMonth: e.target.value })}
                      className="border rounded-md px-3 py-2 text-sm"
                    >
                      <option value="">Mês</option>
                      {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                    <select
                      value={registerData.birthYear}
                      onChange={(e) => setRegisterData({ ...registerData, birthYear: e.target.value })}
                      className="border rounded-md px-3 py-2 text-sm"
                    >
                      <option value="">Ano</option>
                      {years.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <Label>Sexo</Label>
                  <select
                    value={registerData.gender}
                    onChange={(e) => setRegisterData({ ...registerData, gender: e.target.value })}
                    className="w-full border rounded-md px-3 py-2 text-sm mt-1"
                  >
                    <option value="">Selecione (opcional)</option>
                    <option value="masculino">Masculino</option>
                    <option value="feminino">Feminino</option>
                    <option value="outro">Outro</option>
                  </select>
                </div>
                <div>
                  <Label>Senha *</Label>
                  <Input
                    type="password"
                    value={registerData.password}
                    onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                    placeholder="Mínimo 6 caracteres"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Confirmar Senha *</Label>
                  <Input
                    type="password"
                    value={registerData.confirmPassword}
                    onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                    placeholder="Repita a senha"
                    className="mt-1"
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-[#FFD700] hover:bg-[#FFC700] text-gray-900">
                  {loading ? 'Cadastrando...' : 'Criar Conta'}
                </Button>
              </form>
            )}

            {!showForgotPassword && (
              <div className="mt-6 text-center">
                <p className="text-sm text-gray-600">
                  {isLogin ? 'Não tem conta?' : 'Já tem conta?'}{' '}
                  <button onClick={() => { setIsLogin(!isLogin); setMessage({ type: '', text: '' }); }} className="text-[#003366] font-semibold hover:underline">
                    {isLogin ? 'Cadastre-se' : 'Fazer login'}
                  </button>
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CustomerLogin;
